import { DragEvent, ReactNode, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { AlertTriangle, CheckCircle2, FileSearch, FileSpreadsheet, RefreshCw, Upload, X } from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import * as ritiriApi from "../../api/ritiri";
import {
  EsitoImportRitiri,
  FileImportRitiri,
  FileSelezionati,
  ImportRitiriStorico,
  ReportImportRitiri,
  RiepilogoFileImport,
  datiVecchi,
  formattaDataAznext,
} from "../../api/ritiri";
import { useCountUp } from "../../hooks/useCountUp";
import {
  BarraRitiri,
  CardRitiri,
  KpiRitiri,
  PulsanteRitiri,
  SchedeRitiri,
  TestataRitiri,
} from "../../components/ritiri/TestataRitiri";

/**
 * RITIRI VALORI — import degli export AZNext (docs/specs/ritiri-valori.md,
 * §§ 1.4 e 7 punto 6; aspetto: docs/specs/ritiri-valori-riferimento-grafico.md).
 *
 * L'anteprima non scrive nulla. La conferma rimanda esattamente i file usati
 * per l'anteprima (un'istantanea, non la selezione corrente) e il server
 * ricalcola tutto. Qualunque cambio di file annulla l'anteprima; mentre
 * anteprima o conferma sono in corso i file non si possono cambiare, e una
 * risposta di un'anteprima superata si ignora. Così quello che si conferma è
 * quello che si è visto.
 *
 * Con versamenti o prelievi serve la data di generazione dell'export (v1.18):
 * un export più vecchio non riporta indietro le operazioni già aggiornate.
 * Come i file, anche la data cambiata annulla l'anteprima.
 */

const FILE: { campo: FileImportRitiri; etichetta: string; nome: string }[] = [
  { campo: "anagrafica", etichetta: "Anagrafica casseforti", nome: "anagrafica_casseforti.csv" },
  { campo: "versamenti", etichetta: "Versamenti", nome: "tutti_i_versamenti.csv" },
  { campo: "prelievi", etichetta: "Prelievi", nome: "tutti_i_prelievi.csv" },
];

const ETICHETTA_FILE: Record<FileImportRitiri, string> = {
  anagrafica: "Anagrafica",
  versamenti: "Versamenti",
  prelievi: "Prelievi",
};

type Conteggio = "righe_lette" | "nuove" | "aggiornate" | "invariate" | "scartate";

function somma(report: ReportImportRitiri | undefined, campo: Conteggio): number {
  return Object.values(report?.file ?? {}).reduce((acc, f) => acc + (f?.[campo] ?? 0), 0);
}

/** `gg/mm/aaaa hh:mm` nell'ora del browser. */
function fileDelReport(r: ImportRitiriStorico): string {
  return FILE.filter((f) => r.report.file?.[f.campo])
    .map((f) => ETICHETTA_FILE[f.campo])
    .join(", ");
}

function formattaOra(d: Date): string {
  return d
    .toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    .replace(",", "");
}

function dimensione(byte: number): string {
  if (byte < 1024) return `${byte} B`;
  if (byte < 1024 * 1024) return `${(byte / 1024).toLocaleString("it-IT", { maximumFractionDigits: 1 })} KB`;
  return `${(byte / 1024 / 1024).toLocaleString("it-IT", { maximumFractionDigits: 1 })} MB`;
}

// ----------------------------------------------------------------------------
// Zona di rilascio di un file
// ----------------------------------------------------------------------------

function ZonaFile({
  etichetta,
  nome,
  file,
  onScelta,
  disabilitato,
}: {
  etichetta: string;
  nome: string;
  file: File | undefined;
  onScelta: (f: File | null) => void;
  /** Anteprima o conferma in corso: il file non si può cambiare. */
  disabilitato: boolean;
}) {
  const [sopra, setSopra] = useState(false);

  function rilascio(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setSopra(false);
    if (disabilitato) return;
    const f = e.dataTransfer.files?.[0];
    if (f) onScelta(f);
  }

  return (
    <div>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabilitato) setSopra(true);
        }}
        onDragLeave={() => setSopra(false)}
        onDrop={rilascio}
        className={clsx(
          "flex min-h-[132px] flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed px-4 py-5 text-center transition-colors focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 motion-reduce:transition-none",
          disabilitato ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          sopra ? "border-accent bg-ritiri-hover" : file ? "border-primary/40 bg-white" : "border-ritiri-bordo-forte bg-ritiri-th-fondo",
          !disabilitato && !sopra && !file && "hover:bg-ritiri-hover"
        )}
      >
        <input
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          disabled={disabilitato}
          onChange={(e) => {
            onScelta(e.target.files?.[0] ?? null);
            e.target.value = "";
          }}
        />
        <FileSpreadsheet className={clsx("h-6 w-6", file ? "text-primary" : "text-ritiri-th-testo")} aria-hidden="true" />
        <span className="text-sm font-bold text-ink">{etichetta}</span>
        {file ? (
          <span className="font-plex text-xs text-ink">
            {file.name} · {dimensione(file.size)}
          </span>
        ) : (
          <span className="text-xs text-ritiri-th-testo">
            Trascina qui <span className="font-plex">{nome}</span> oppure fai clic per sceglierlo
          </span>
        )}
      </label>
      {file && (
        <button
          type="button"
          onClick={() => onScelta(null)}
          disabled={disabilitato}
          className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-ritiri-th-testo hover:text-ink disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:text-ritiri-th-testo"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
          Togli {etichetta.toLowerCase()}
        </button>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Report dell'anteprima / dell'import
// ----------------------------------------------------------------------------

function VoceConteggio({ etichetta, valore, totale, tono }: { etichetta: string; valore: number; totale: number; tono: "primary" | "accent" | "caldo" | "neutro" }) {
  const animato = useCountUp(valore);
  return (
    <div className="grid grid-cols-[88px_1fr_56px] items-center gap-3 text-sm">
      <span className="text-ritiri-th-testo">{etichetta}</span>
      <BarraRitiri valore={valore} massimo={totale} tono={tono} />
      <span className="text-right font-plex font-semibold text-ink">{animato.toLocaleString("it-IT")}</span>
    </div>
  );
}

function CardFile({ etichetta, r }: { etichetta: string; r: RiepilogoFileImport }) {
  const lette = useCountUp(r.righe_lette);
  return (
    <div className="rounded-xl border border-ritiri-bordo p-4">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <span className="font-bold text-ink">{etichetta}</span>
        <span className="text-xs text-ritiri-th-testo">
          <span className="font-plex text-sm font-semibold text-ink">{lette.toLocaleString("it-IT")}</span> righe lette
        </span>
      </div>
      <div className="space-y-2">
        <VoceConteggio etichetta="Nuove" valore={r.nuove} totale={r.righe_lette} tono="primary" />
        <VoceConteggio etichetta="Aggiornate" valore={r.aggiornate} totale={r.righe_lette} tono="accent" />
        <VoceConteggio etichetta="Invariate" valore={r.invariate} totale={r.righe_lette} tono="neutro" />
        <VoceConteggio etichetta="Scartate" valore={r.scartate} totale={r.righe_lette} tono="caldo" />
      </div>
      {r.avvisi_totali > 0 && (
        <p className="mt-3 text-xs font-semibold text-ink">
          {r.avvisi_totali} {r.avvisi_totali === 1 ? "avviso" : "avvisi"} (valori salvati vuoti)
        </p>
      )}
    </div>
  );
}

function Th({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={clsx(
        "bg-ritiri-th-fondo px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-ritiri-th-testo",
        className
      )}
    >
      {children}
    </th>
  );
}

function EtichettaTipo({ tipo }: { tipo: "scarto" | "avviso" }) {
  return (
    <span
      className={clsx(
        "rounded-full px-2 py-0.5 text-xs font-semibold",
        tipo === "scarto" ? "bg-ritiri-caldo/10 text-ritiri-caldo" : "bg-accent/20 text-ink"
      )}
    >
      {tipo === "scarto" ? "Scartata" : "Avviso"}
    </span>
  );
}

/**
 * Su telefono (sotto i 640px) le tabelle diventano una scheda per riga, con le
 * coppie etichetta: valore, invece di scorrere di lato.
 */
function SchedaRiga({ voci, className }: { voci: [string, ReactNode][]; className?: string }) {
  return (
    <li className={clsx("rounded-xl border border-ritiri-bordo bg-white p-3", className)}>
      <dl className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
        {voci.map(([dt, dd]) => (
          <div key={dt} className="contents">
            <dt className="text-[11px] font-bold uppercase tracking-wide text-ritiri-th-testo">{dt}</dt>
            <dd className="min-w-0 break-words text-ink">{dd}</dd>
          </div>
        ))}
      </dl>
    </li>
  );
}

function TabellaScarti({ report }: { report: ReportImportRitiri }) {
  const righe = FILE.flatMap(({ campo }) => {
    const r = report.file[campo];
    if (!r) return [];
    return [
      ...r.dettaglio_scarti.map((s) => ({ file: campo, riga: s.riga, tipo: "scarto" as const, testo: s.motivo })),
      ...r.avvisi.map((a) => ({ file: campo, riga: a.riga, tipo: "avviso" as const, testo: a.messaggio })),
    ];
  });
  const troncato = FILE.some(({ campo }) => {
    const r = report.file[campo];
    return r && (r.dettaglio_scarti.length < r.scartate || r.avvisi.length < r.avvisi_totali);
  });
  if (righe.length === 0) {
    return <p className="text-sm text-ritiri-th-testo">Nessuna riga scartata e nessun avviso.</p>;
  }
  return (
    <div>
      <ul className="max-h-96 space-y-2 overflow-auto sm:hidden">
        {righe.map((x, i) => (
          <SchedaRiga
            key={`${x.file}-${x.tipo}-${x.riga}-${i}`}
            voci={[
              ["File", ETICHETTA_FILE[x.file]],
              ["Riga", <span className="font-plex">{x.riga}</span>],
              ["Tipo", <EtichettaTipo tipo={x.tipo} />],
              ["Dettaglio", <span className="text-ink/80">{x.testo}</span>],
            ]}
          />
        ))}
      </ul>
      <div className="hidden max-h-80 overflow-auto rounded-xl border border-ritiri-bordo sm:block">
        <table className="w-full min-w-[520px] border-collapse text-sm">
          <thead className="sticky top-0">
            <tr>
              <Th>File</Th>
              <Th>Riga</Th>
              <Th>Tipo</Th>
              <Th>Dettaglio</Th>
            </tr>
          </thead>
          <tbody>
            {righe.map((x, i) => (
              <tr key={`${x.file}-${x.tipo}-${x.riga}-${i}`} className="ritiri-riga border-t border-ritiri-bordo hover:bg-ritiri-hover">
                <td className="px-3 py-2">{ETICHETTA_FILE[x.file]}</td>
                <td className="px-3 py-2 font-plex">{x.riga}</td>
                <td className="px-3 py-2">
                  <EtichettaTipo tipo={x.tipo} />
                </td>
                <td className="px-3 py-2 text-ink/80">{x.testo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {troncato && <p className="mt-2 text-xs text-ritiri-th-testo">Il dettaglio riga per riga è limitato alle prime 200 voci per file.</p>}
    </div>
  );
}

function Elenco({ titolo, voci }: { titolo: string; voci: string[] }) {
  if (voci.length === 0) return null;
  return (
    <details className="rounded-xl border border-ritiri-bordo px-4 py-3 text-sm">
      <summary className="cursor-pointer font-semibold text-ink">
        {titolo} <span className="font-plex">({voci.length})</span>
      </summary>
      <ul className="mt-2 max-h-56 space-y-0.5 overflow-auto font-plex text-xs text-ink/80">
        {voci.map((v) => (
          <li key={v}>{v}</li>
        ))}
      </ul>
    </details>
  );
}

function Report({ esito }: { esito: EsitoImportRitiri }) {
  const { report } = esito;
  const presenti = FILE.filter((f) => report.file[f.campo]);
  const avvisi = report.avvisi_export ?? [];
  const conOperazioni = !!(report.file.versamenti || report.file.prelievi);
  return (
    <div className="space-y-5">
      {avvisi.length > 0 && (
        <ul className="space-y-1.5 rounded-xl border border-accent bg-accent/10 p-3 text-sm text-ink" data-avvisi="export">
          {avvisi.map((a) => (
            <li key={a} className="flex gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-accent-dark" aria-hidden="true" />
              <span>{a}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="grid gap-4 md:grid-cols-3">
        {presenti.map(({ campo, etichetta }) => (
          <CardFile key={campo} etichetta={etichetta} r={report.file[campo]!} />
        ))}
      </div>

      <dl className="grid grid-cols-2 gap-3 text-sm lg:grid-cols-4">
        {[
          ["Operazione più recente", formattaDataAznext(report.data_operazione_max)],
          ["Ricezione più recente", formattaDataAznext(report.data_ricezione_max)],
          ["Casseforti non collegate all'anagrafica", String(report.casseforti_non_collegate.totale)],
          ["Righe minime (anagrafica AZNext mancante)", String(report.righe_minime.length)],
          ...(conOperazioni && report.generato_il !== undefined
            ? [
                ["Export generato il", formattaDataAznext(report.generato_il ?? null)],
                ["Operazioni non aggiornate: dato più vecchio di quello salvato", String(report.operazioni_piu_vecchie ?? 0)],
              ]
            : []),
        ].map(([dt, dd]) => (
          <div key={dt} className="rounded-xl bg-ritiri-th-fondo p-3">
            <dt className="text-xs text-ritiri-th-testo">{dt}</dt>
            <dd className="mt-1 font-plex font-semibold text-ink">{dd}</dd>
          </div>
        ))}
      </dl>

      <div>
        <h3 className="mb-2 text-sm font-bold text-ink">Righe scartate e avvisi</h3>
        <TabellaScarti report={report} />
      </div>

      <div className="space-y-2">
        <Elenco titolo="Casseforti non collegate all'anagrafica" voci={report.casseforti_non_collegate.id_macchina} />
        <Elenco titolo="Righe minime create in anagrafica AZNext" voci={report.righe_minime} />
        <Elenco
          titolo="Doppioni nell'anagrafica AZNext"
          voci={report.doppioni_anagrafica.map((d) => `${d.id_macchina}: righe ${d.righe.join(", ")} → usata la ${d.riga_scelta}`)}
        />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Pagina
// ----------------------------------------------------------------------------

export default function ImportRitiri() {
  const { showToast } = useToast();
  const [file, setFile] = useState<FileSelezionati>({});
  // Data e ora di generazione dell'export, `YYYY-MM-DDTHH:MM` in ora italiana (v1.18).
  const [generatoIl, setGeneratoIl] = useState("");
  // Quella usata per l'anteprima mostrata: la conferma invia questa.
  const [generatoAnteprima, setGeneratoAnteprima] = useState("");
  const [esito, setEsito] = useState<EsitoImportRitiri | null>(null);
  // File usati per l'anteprima mostrata: la conferma invia questi, non `file`.
  const [fileAnteprima, setFileAnteprima] = useState<FileSelezionati | null>(null);
  // Ora della conferma riuscita, mostrata accanto a "Conferma import".
  const [importatoIl, setImportatoIl] = useState<Date | null>(null);
  // Versione della selezione: ogni cambio di file e ogni anteprima la fanno
  // avanzare, e una risposta con una versione superata si ignora.
  const versione = useRef(0);
  const [inCorso, setInCorso] = useState<"anteprima" | "conferma" | null>(null);
  const [storico, setStorico] = useState<ImportRitiriStorico[]>([]);
  const [aggiornatiAl, setAggiornatiAl] = useState<string | null>(null);
  const [storicoInCaricamento, setStoricoInCaricamento] = useState(true);

  async function caricaStorico() {
    setStoricoInCaricamento(true);
    try {
      const dati = await ritiriApi.storicoImport();
      setStorico(dati.storico);
      setAggiornatiAl(dati.dati_aggiornati_al);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare lo storico degli import."), "error");
    } finally {
      setStoricoInCaricamento(false);
    }
  }

  useEffect(() => {
    caricaStorico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nessunFile = !Object.values(file).some(Boolean);
  const conOperazioni = !!(file.versamenti || file.prelievi);
  const mancaData = conOperazioni && generatoIl === "";

  /** Come un cambio di file: la data diversa annulla l'anteprima. */
  function scegliGeneratoIl(valore: string) {
    if (inCorso) return;
    versione.current += 1;
    setGeneratoIl(valore);
    setEsito(null);
    setFileAnteprima(null);
    setImportatoIl(null);
  }

  function scegliFile(campo: FileImportRitiri, f: File | null) {
    if (inCorso) return;
    versione.current += 1;
    setFile((prec) => ({ ...prec, [campo]: f ?? undefined }));
    // Un file diverso annulla l'anteprima: si conferma solo quello che si è visto.
    setEsito(null);
    setFileAnteprima(null);
    setImportatoIl(null);
  }

  async function anteprima() {
    const mia = ++versione.current;
    const istantanea = { ...file };
    // Senza operazioni la data non serve e non si invia.
    const data = istantanea.versamenti || istantanea.prelievi ? generatoIl : "";
    setInCorso("anteprima");
    setEsito(null);
    setFileAnteprima(null);
    setImportatoIl(null);
    try {
      const risposta = await ritiriApi.anteprimaImport(istantanea, data);
      if (mia !== versione.current) return;
      setEsito(risposta);
      setFileAnteprima(istantanea);
      setGeneratoAnteprima(data);
    } catch (err) {
      if (mia !== versione.current) return;
      showToast(getErrorMessage(err, "Impossibile leggere i file."), "error");
    } finally {
      setInCorso(null);
    }
  }

  async function conferma() {
    if (!fileAnteprima) return;
    const mia = versione.current;
    setInCorso("conferma");
    try {
      const risposta = await ritiriApi.confermaImport(fileAnteprima, generatoAnteprima);
      if (mia === versione.current) {
        setEsito(risposta);
        setFileAnteprima(null);
        setImportatoIl(new Date());
      }
      showToast("Import completato.", "success");
      await caricaStorico();
    } catch (err) {
      showToast(getErrorMessage(err, "Import non riuscito: nessun dato è stato scritto."), "error");
    } finally {
      setInCorso(null);
    }
  }

  // Suggerimento dopo l'anteprima: la ricezione più recente nei file, come data
  // del campo. È solo un limite inferiore: l'export è stato generato dopo.
  const ricezioneFile = esito?.anteprima ? esito.report.data_ricezione_max : null;
  const suggerita = ricezioneFile ? ricezioneFile.slice(0, 16).replace(" ", "T") : null;

  // KPI della testata: l'ultima anteprima o l'ultimo import di questa pagina,
  // altrimenti l'ultimo import dello storico.
  const fonteKpi = esito?.report ?? storico[0]?.report;
  const notaKpi = esito
    ? esito.anteprima
      ? "Ultima anteprima (non ancora salvata)"
      : "Ultimo import"
    : storico[0]
      ? `Ultimo import, ${new Date(storico[0].created_at).toLocaleString("it-IT")}`
      : "Nessun import ancora eseguito";
  const kpi: KpiRitiri[] = [
    { etichetta: "Righe lette", valore: fonteKpi ? somma(fonteKpi, "righe_lette") : null },
    { etichetta: "Nuove", valore: fonteKpi ? somma(fonteKpi, "nuove") : null },
    { etichetta: "Aggiornate", valore: fonteKpi ? somma(fonteKpi, "aggiornate") : null },
    { etichetta: "Invariate", valore: fonteKpi ? somma(fonteKpi, "invariate") : null },
    { etichetta: "Scartate", valore: fonteKpi ? somma(fonteKpi, "scartate") : null, caldo: !!fonteKpi && somma(fonteKpi, "scartate") > 0 },
  ];

  const vecchi = datiVecchi(aggiornatiAl);
  const pillola = storicoInCaricamento
    ? null
    : aggiornatiAl
      ? {
          testo: `Dati aggiornati al ${formattaDataAznext(aggiornatiAl)}${vecchi ? " · più vecchi di 24 ore" : ""}`,
          allarme: vecchi,
        }
      : { testo: "Nessun dato AZNext importato", allarme: true };

  return (
    <div>
      <TestataRitiri
        sopratitolo="Ritiri valori · import dati AZNext"
        titolo="Import dati AZNext"
        sottotitolo="Carica gli export del portale Cash Management (CSV separati da punto e virgola). Basta anche un solo file: l'anagrafica, se c'è, si elabora per prima. L'anteprima non scrive nulla e reimportare gli stessi file non crea doppioni."
        pillola={pillola}
        kpi={kpi}
        notaKpi={notaKpi}
      />
      <SchedeRitiri schede={[{ etichetta: "Import dati", attiva: true }]} />

      <div className="-mx-4 space-y-6 px-4 pb-10 pt-[22px] sm:-mx-6 sm:px-10">
        <CardRitiri titolo="File da importare">
          <div className="grid gap-4 md:grid-cols-3">
            {FILE.map(({ campo, etichetta, nome }) => (
              <ZonaFile
                key={campo}
                etichetta={etichetta}
                nome={nome}
                file={file[campo]}
                onScelta={(f) => scegliFile(campo, f)}
                disabilitato={inCorso !== null}
              />
            ))}
          </div>

          <div className="mt-5 max-w-xl">
            <label htmlFor="generato-il" className="block text-xs font-bold text-ritiri-th-testo">
              Export generato il{conOperazioni && <span aria-hidden="true"> *</span>}
            </label>
            <input
              id="generato-il"
              type="datetime-local"
              required={conOperazioni}
              aria-describedby="generato-il-aiuto"
              disabled={inCorso !== null}
              value={generatoIl}
              onChange={(e) => scegliGeneratoIl(e.target.value)}
              className="mt-1 h-10 w-full max-w-xs rounded-[11px] border border-ritiri-bordo-forte bg-white px-3 font-plex text-sm text-ink disabled:cursor-not-allowed disabled:opacity-60"
            />
            <p id="generato-il-aiuto" className="mt-1 text-xs text-ritiri-th-testo">
              Data e ora (italiana) in cui sono stati estratti i dati da AZNext. Obbligatoria con versamenti o prelievi: un export più
              vecchio non riporta indietro le operazioni già aggiornate.
            </p>
            {suggerita && suggerita !== generatoIl && (
              <button
                type="button"
                onClick={() => scegliGeneratoIl(suggerita)}
                disabled={inCorso !== null}
                className="mt-1 text-xs font-semibold text-ink underline underline-offset-2 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
              >
                Ricezione più recente nei file: {formattaDataAznext(ricezioneFile)}. Usala come data di generazione
              </button>
            )}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <PulsanteRitiri
              type="button"
              variante="secondario"
              onClick={anteprima}
              disabled={nessunFile || mancaData || inCorso !== null}
              inCorso={inCorso === "anteprima"}
            >
              {inCorso !== "anteprima" && <FileSearch className="h-4 w-4" aria-hidden="true" />}
              Anteprima
            </PulsanteRitiri>
            <PulsanteRitiri
              type="button"
              onClick={conferma}
              disabled={!esito?.anteprima || !fileAnteprima || inCorso !== null}
              inCorso={inCorso === "conferma"}
            >
              {inCorso !== "conferma" && <Upload className="h-4 w-4" aria-hidden="true" />}
              Conferma import
            </PulsanteRitiri>
            {esito && !esito.anteprima && (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink">
                <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                Import salvato{importatoIl && <> il <span className="font-plex">{formattaOra(importatoIl)}</span></>}
              </span>
            )}
            {!esito && !nessunFile && (
              <span className="text-sm text-ritiri-th-testo">
                {mancaData ? "Indica quando è stato generato l'export, poi fai l'anteprima." : "Fai l'anteprima per poter confermare."}
              </span>
            )}
          </div>
        </CardRitiri>

        {esito && (
          <div aria-live="polite" className="ritiri-rise">
            <CardRitiri titolo={esito.anteprima ? "Anteprima" : "Risultato dell'import"}>
              <Report esito={esito} />
            </CardRitiri>
          </div>
        )}

        <CardRitiri
          titolo="Storico import"
          azioni={
            <PulsanteRitiri type="button" variante="secondario" onClick={caricaStorico} inCorso={storicoInCaricamento} className="h-9 px-3.5">
              {!storicoInCaricamento && <RefreshCw className="h-4 w-4" aria-hidden="true" />}
              Aggiorna
            </PulsanteRitiri>
          }
        >
          {storico.length === 0 ? (
            <p className="rounded-xl border border-ritiri-bordo px-3 py-6 text-center text-sm text-ritiri-th-testo sm:hidden">
              {storicoInCaricamento ? "Caricamento…" : "Nessun import ancora eseguito."}
            </p>
          ) : (
            <ul className="space-y-2 sm:hidden">
              {storico.map((r) => {
                const scartate = somma(r.report, "scartate");
                return (
                  <SchedaRiga
                    key={r.id}
                    voci={[
                      ["Quando", <span className="font-plex text-xs">{new Date(r.created_at).toLocaleString("it-IT")}</span>],
                      ["Chi", r.user?.full_name ?? r.user?.email ?? "—"],
                      ["Origine", r.origine === "collector" ? "Collector" : "Manuale"],
                      ["File", fileDelReport(r)],
                      ["Nuove", <span className="font-plex">{somma(r.report, "nuove").toLocaleString("it-IT")}</span>],
                      ["Aggiornate", <span className="font-plex">{somma(r.report, "aggiornate").toLocaleString("it-IT")}</span>],
                      [
                        "Scartate",
                        <span className={clsx("font-plex", scartate > 0 && "font-semibold text-ritiri-caldo")}>{scartate.toLocaleString("it-IT")}</span>,
                      ],
                      ["Ricezione più recente", <span className="font-plex text-xs">{formattaDataAznext(r.data_ricezione_max)}</span>],
                      ["Export generato il", <span className="font-plex text-xs">{formattaDataAznext(r.generato_il)}</span>],
                    ]}
                  />
                );
              })}
            </ul>
          )}
          <div className="hidden overflow-x-auto rounded-xl border border-ritiri-bordo sm:block">
            <table className="w-full min-w-[820px] border-collapse text-sm">
              <thead>
                <tr>
                  <Th>Quando</Th>
                  <Th>Chi</Th>
                  <Th>Origine</Th>
                  <Th>File</Th>
                  <Th className="text-right">Nuove</Th>
                  <Th className="text-right">Aggiornate</Th>
                  <Th className="text-right">Scartate</Th>
                  <Th>Ricezione più recente</Th>
                  <Th>Export generato il</Th>
                </tr>
              </thead>
              <tbody>
                {storico.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-center text-ritiri-th-testo">
                      {storicoInCaricamento ? "Caricamento…" : "Nessun import ancora eseguito."}
                    </td>
                  </tr>
                )}
                {storico.map((r) => {
                  const scartate = somma(r.report, "scartate");
                  return (
                    <tr key={r.id} className="ritiri-riga border-t border-ritiri-bordo hover:bg-ritiri-hover">
                      <td className="px-3 py-2.5 font-plex text-xs">{new Date(r.created_at).toLocaleString("it-IT")}</td>
                      <td className="px-3 py-2.5">{r.user?.full_name ?? r.user?.email ?? "—"}</td>
                      <td className="px-3 py-2.5">{r.origine === "collector" ? "Collector" : "Manuale"}</td>
                      <td className="px-3 py-2.5">{fileDelReport(r)}</td>
                      <td className="px-3 py-2.5 text-right font-plex">{somma(r.report, "nuove").toLocaleString("it-IT")}</td>
                      <td className="px-3 py-2.5 text-right font-plex">{somma(r.report, "aggiornate").toLocaleString("it-IT")}</td>
                      <td className={clsx("px-3 py-2.5 text-right font-plex", scartate > 0 && "font-semibold text-ritiri-caldo")}>
                        {scartate.toLocaleString("it-IT")}
                      </td>
                      <td className="px-3 py-2.5 font-plex text-xs">{formattaDataAznext(r.data_ricezione_max)}</td>
                      <td className="px-3 py-2.5 font-plex text-xs">{formattaDataAznext(r.generato_il)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardRitiri>
      </div>
    </div>
  );
}
